// src/routes/AppRoutes.jsx
import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Spin } from 'antd';

import MainLayout from '../layouts/MainLayout';
import AuthLayout from '../layouts/AuthLayout';
import AdminLayout from '../layouts/AdminLayout';
import PublicLayout from '../layouts/PublicLayout';
import PrivateRoute from './PrivateRoute';
import AdminRoute from './AdminRoute';
import { ROUTES } from '../constants/routes';

/* ------------------------- PUBLIC ------------------------- */
const Home = lazy(() => import('../pages/home'));
const About = lazy(() => import('../pages/legal/About'));
const Contact = lazy(() => import('../pages/legal/Contact'));
const Terms = lazy(() => import('../pages/legal/Terms'));
const Privacy = lazy(() => import('../pages/legal/Privacy'));
const Refund = lazy(() => import('../pages/legal/Refund'));
const FAQ = lazy(() => import('../pages/legal/FAQ'));
const Cookies = lazy(() => import('../pages/legal/Cookies'));
const Cart = lazy(() => import('../pages/cart/Cart'));

/* ------------------------- AUTH --------------------------- */
const Login = lazy(() => import('../pages/auth/Login'));
const Register = lazy(() => import('../pages/auth/Register'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'));
const TwoFactorAuth = lazy(() => import('../pages/auth/TwoFactorAuth'));
const VerifyEmail = lazy(() => import('../pages/auth/VerifyEmail'));

/* ------------------------ DASHBOARD ----------------------- */
const Dashboard = lazy(() => import('../pages/dashboard/Dashboard'));
const Portfolio = lazy(() => import('../pages/dashboard/Portfolio'));
const Announcements = lazy(() => import('../pages/dashboard/Announcements'));
const Referrals = lazy(() => import('../pages/dashboard/Referrals'));
const NotificationsInbox = lazy(() => import('../pages/dashboard/Notifications'));
const Checkout = lazy(() => import('../pages/checkout/Checkout'));


/* ------------------------- MARKET ------------------------- */
const Market = lazy(() => import('../pages/market/Market'));
const CoinDetails = lazy(() => import('../pages/market/CoinDetails'));
const Watchlist = lazy(() => import('../pages/market/Watchlist'));

/* -------------------------- OTC --------------------------- */
const OTCDesk = lazy(() => import('../pages/otc/OTCDesk'));
const CreateOrder = lazy(() => import('../pages/otc/CreateOrder'));
const OrderBook = lazy(() => import('../pages/otc/OrderBook'));
const MyQuotes = lazy(() => import('../pages/otc/MyQuotes'));
const TradeHistory = lazy(() => import('../pages/otc/TradeHistory'));
const Counterparties = lazy(() => import('../pages/otc/Counterparties'));

/* ------------------------- WALLET ------------------------- */
const Wallets = lazy(() => import('../pages/wallet/Wallets'));
const Deposit = lazy(() => import('../pages/wallet/Deposit'));
const Withdraw = lazy(() => import('../pages/wallet/Withdraw'));
const Transfer = lazy(() => import('../pages/wallet/Transfer'));
const AddressBook = lazy(() => import('../pages/wallet/AddressBook'));
const WalletDetails = lazy(() => import('../pages/wallet/WalletDetails'));

/* ------------------------- ORDERS ------------------------- */
const OpenOrders = lazy(() => import('../pages/orders/OpenOrders'));
const OrderHistory = lazy(() => import('../pages/orders/OrderHistory'));
const OrderDetails = lazy(() => import('../pages/orders/OrderDetails'));

/* ---------------------- TRANSACTIONS ---------------------- */
const Transactions = lazy(() => import('../pages/transactions/Transactions'));
const TransactionDetails = lazy(() => import('../pages/transactions/TransactionDetails'));
const Invoices = lazy(() => import('../pages/transactions/Invoices'));

/* --------------------------- KYC -------------------------- */
const KYCVerification = lazy(() => import('../pages/kyc/KYCVerification'));
const IdentityUpload = lazy(() => import('../pages/kyc/IdentityUpload'));
const KYCStatus = lazy(() => import('../pages/kyc/KYCStatus'));
const Compliance = lazy(() => import('../pages/kyc/Compliance'));

/* ------------------------ SETTINGS ------------------------ */
const Profile = lazy(() => import('../pages/settings/Profile'));
const Security = lazy(() => import('../pages/settings/Security'));
const APIKeys = lazy(() => import('../pages/settings/APIKeys'));
const Notifications = lazy(() => import('../pages/settings/Notifications'));
const Preferences = lazy(() => import('../pages/settings/Preferences'));

/* -------------------------- ADMIN ------------------------- */
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('../pages/admin/Users'));
const AdminOrders = lazy(() => import('../pages/admin/Orders'));
const AdminKYC = lazy(() => import('../pages/admin/KYCReview'));
const AdminLiquidity = lazy(() => import('../pages/admin/Liquidity'));
const AdminReports = lazy(() => import('../pages/admin/Reports'));
const TrackOrder = lazy(() => import('../pages/track/TrackOrder'));
const UserDetail = lazy(() => import('../pages/admin/UserDetail'));
const OrderDetail = lazy(() => import('../pages/admin/OrderDetail'));
const Products = lazy(() => import('../pages/admin/Products'));
const AdminAnnouncements = lazy(() => import('../pages/admin/Announcements'));
const Brands = lazy(() => import('../pages/admin/Brands'));
const ProductImages = lazy(() => import('../pages/admin/ProductImages'));
const Audit = lazy(() => import('../pages/admin/Audit'));
const AdminSettings = lazy(() => import('../pages/admin/Settings'));
const CheckoutSuccess = lazy(() => import('../pages/checkout/CheckoutSuccess'));
const ReferralLanding = lazy(() => import('../pages/referral/ReferralLanding'));
const Translations = lazy(() => import('../pages/admin/Translations'));
const ReportsAnalytics = lazy(() => import('../pages/admin/ReportsAnalytics'));
const HelpCenter = lazy(() => import('../pages/help/HelpCenter'));
const HelpArticle = lazy(() => import('../pages/help/HelpArticle'));
const Backups = lazy(() => import('../pages/admin/Backups'));
const Coins = lazy(() => import('../pages/admin/Coins'));



/* ------------------------- ERRORS ------------------------- */
const NotFound = lazy(() => import('../pages/errors/NotFound'));
const ServerError = lazy(() => import('../pages/errors/ServerError'));
const Unauthorized = lazy(() => import('../pages/errors/Unauthorized'));

/* ------------------------ FALLBACK ------------------------ */
const Loading = (
    <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Spin size="large" />
    </div>
);

export default function AppRoutes() {
    return (
        <Suspense fallback={Loading}>
            <Routes>
                {/* ==================== PUBLIC ==================== */}

                {/* Home — standalone (no shared layout chrome) */}
                <Route path={ROUTES.HOME} element={<Home />} />

                {/* Public marketing pages share PublicLayout (header + footer) */}
                <Route element={<PublicLayout />}>
                    <Route path={ROUTES.ABOUT} element={<About />} />
                    <Route path={ROUTES.CONTACT} element={<Contact />} />
                    <Route path={ROUTES.TERMS} element={<Terms />} />
                    <Route path={ROUTES.PRIVACY} element={<Privacy />} />
                    <Route path="/refund" element={<Refund />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/cookies" element={<Cookies />} />
                    <Route path="/track" element={<TrackOrder />} />
                    <Route path="/r/:code" element={<ReferralLanding />} />
                    <Route path="/help" element={<HelpCenter />} />
                    <Route path="/help/:slug" element={<HelpArticle />} />


                </Route>

                {/* ===================== AUTH ===================== */}
                <Route element={<AuthLayout />}>
                    <Route path={ROUTES.LOGIN} element={<Login />} />
                    <Route path={ROUTES.REGISTER} element={<Register />} />
                    <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
                    <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
                    <Route path={ROUTES.TWO_FA} element={<TwoFactorAuth />} />
                    <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
                </Route>

                {/* =================== PROTECTED ================== */}
                <Route element={<PrivateRoute />}>
                    <Route element={<MainLayout />}>
                        {/* Dashboard */}
                        <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                        <Route path={ROUTES.PORTFOLIO} element={<Portfolio />} />
                        <Route path={ROUTES.ANNOUNCEMENTS} element={<Announcements />} />
                        <Route path={ROUTES.REFERRALS} element={<Referrals />} />
                        <Route path={ROUTES.NOTIFICATIONS_INBOX} element={<NotificationsInbox />} />
                        <Route path={ROUTES.CHECKOUT} element={<Checkout />} />
                        <Route path="/cart" element={<Cart />} />
                        {/* Market */}
                        <Route path={ROUTES.MARKET} element={<Market />} />
                        <Route path={ROUTES.COIN_DETAILS} element={<CoinDetails />} />
                        <Route path={ROUTES.WATCHLIST} element={<Watchlist />} />

                        {/* OTC */}
                        <Route path={ROUTES.OTC_DESK} element={<OTCDesk />} />
                        <Route path={ROUTES.OTC_CREATE} element={<CreateOrder />} />
                        <Route path={ROUTES.OTC_ORDERBOOK} element={<OrderBook />} />
                        <Route path={ROUTES.OTC_QUOTES} element={<MyQuotes />} />
                        <Route path={ROUTES.OTC_HISTORY} element={<TradeHistory />} />
                        <Route path={ROUTES.OTC_COUNTERPARTIES} element={<Counterparties />} />

                        {/* Wallet */}
                        <Route path={ROUTES.WALLETS} element={<Wallets />} />
                        <Route path={ROUTES.DEPOSIT} element={<Deposit />} />
                        <Route path={ROUTES.WITHDRAW} element={<Withdraw />} />
                        <Route path={ROUTES.TRANSFER} element={<Transfer />} />
                        <Route path={ROUTES.ADDRESS_BOOK} element={<AddressBook />} />
                        <Route path={ROUTES.WALLET_DETAILS} element={<WalletDetails />} />

                        {/* Orders */}
                        <Route path={ROUTES.OPEN_ORDERS} element={<OpenOrders />} />
                        <Route path={ROUTES.ORDER_HISTORY} element={<OrderHistory />} />
                        <Route path={ROUTES.ORDER_DETAILS} element={<OrderDetails />} />

                        {/* Transactions */}
                        <Route path={ROUTES.TRANSACTIONS} element={<Transactions />} />
                        <Route path={ROUTES.TRANSACTION_DETAILS} element={<TransactionDetails />} />
                        <Route path={ROUTES.INVOICES} element={<Invoices />} />

                        {/* KYC */}
                        <Route path={ROUTES.KYC} element={<KYCVerification />} />
                        <Route path={ROUTES.KYC_UPLOAD} element={<IdentityUpload />} />
                        <Route path={ROUTES.KYC_STATUS} element={<KYCStatus />} />
                        <Route path={ROUTES.COMPLIANCE} element={<Compliance />} />


                        {/* Status */}
                        <Route path="/checkout/success" element={<CheckoutSuccess />} />


                        {/* Settings */}
                        <Route path={ROUTES.PROFILE} element={<Profile />} />
                        <Route path={ROUTES.SECURITY} element={<Security />} />
                        <Route path={ROUTES.API_KEYS} element={<APIKeys />} />
                        <Route path={ROUTES.NOTIFICATIONS} element={<Notifications />} />
                        <Route path={ROUTES.PREFERENCES} element={<Preferences />} />
                    </Route>
                </Route>

                {/* ====================== ADMIN =================== */}
                <Route element={<AdminRoute />}>
                    <Route element={<AdminLayout />}>
                        <Route path={ROUTES.ADMIN} element={<AdminDashboard />} />
                        <Route path={ROUTES.ADMIN_USERS} element={<AdminUsers />} />
                        <Route path={ROUTES.ADMIN_ORDERS} element={<AdminOrders />} />
                        <Route path={ROUTES.ADMIN_KYC} element={<AdminKYC />} />
                        <Route path={ROUTES.ADMIN_LIQUIDITY} element={<AdminLiquidity />} />
                        <Route path={ROUTES.ADMIN_REPORTS} element={<AdminReports />} />
                        <Route path={`${ROUTES.ADMIN_USERS}/:id`} element={<UserDetail />} />
                        <Route path={`${ROUTES.ADMIN_ORDERS}/:id`} element={<OrderDetail />} />
                        <Route path="/admin/products" element={<Products />} />
                        <Route path="/admin/announcements" element={<AdminAnnouncements />} />
                        <Route path="/admin/brands" element={<Brands />} />
                        <Route path="/admin/products/:id/images" element={<ProductImages />} />
                        <Route path="/admin/audit" element={<Audit />} />
                        <Route path="/admin/settings" element={<AdminSettings />} />
                        <Route path="/admin/translations" element={<Translations />} />
                        <Route path="/admin/reports/analytics" element={<ReportsAnalytics />} />
                        <Route path="/admin/backups" element={<Backups />} />
                        <Route path="/admin/coins" element={<Coins />} />
                    </Route>
                </Route>

                {/* ===================== ERRORS =================== */}
                <Route path={ROUTES.SERVER_ERROR} element={<ServerError />} />
                <Route path={ROUTES.UNAUTHORIZED} element={<Unauthorized />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Suspense>
    );
}